/**
 * @desc        FarmDeck local Dexie database.
 *              Single source of truth for the IndexedDB instance and schema
 *              used by both React hooks (via DBProvider) and plain service
 *              modules (via getDb()).
 *
 *              Version baseline is 100 so that we clear the unpredictable
 *              on-disk version numbers left by the previous homegrown
 *              changeSchema helper. Future migrations: declare version(101)
 *              with an .upgrade(tx => ...) block.
 */

import Dexie from 'dexie'

export const FARMDECK_DB_NAME = process.env.NODE_ENV === 'development' ? 'fmd-dev' : 'fmd-prod'
export const FARMDECK_DB_VERSION = 101
const BASELINE_VERSION = 100

let dbPromise = null
let dbInstance = null

const defineSchema = db => {
    db.version(BASELINE_VERSION).stores({
        activity: '++id,event,action,timestamp',
        assets: '++id,tenant,type,feature,timestamp',
        settings: '++id,tenant,farm,feature,timestamp',
        mobQueries: 'id,queryResult,timestamp',
        rfidSessions: 'id, tenantId, status, finishedAt, startedAt, [tenantId+status]',
        rfidRecords: '[sessionId+id], sessionId, timestamp, fields.eid',
    })
    // Asset import rows that cannot become assets yet (AR-MIG-02). Additive: no upgrade step, nothing existing moves.
    db.version(FARMDECK_DB_VERSION).stores({
        importDrafts: 'id, tenant, batchId, status, [tenant+batchId], [tenant+status]',
    })
}

const isDbUsable = db => {
    if (!db) return false
    try {
        return db.isOpen()
    } catch {
        return false
    }
}

const openDb = async () => {
    const db = new Dexie(FARMDECK_DB_NAME)
    defineSchema(db)
    await db.open()
    if (typeof navigator !== 'undefined' && navigator.storage && typeof navigator.storage.persist === 'function') {
        navigator.storage.persist().catch(() => {})
    }
    return db
}

/**
 * Lazy async accessor for the FarmDeck Dexie instance.
 * Returns the same Promise across callers; safe to call repeatedly.
 * Self-heals if the underlying DB was closed (HMR, unexpected).
 */
export const getDb = () => {
    if (dbInstance && !isDbUsable(dbInstance)) {
        dbInstance = null
        dbPromise = null
    }

    if (!dbPromise) {
        dbPromise = openDb()
            .then(db => {
                dbInstance = db
                return db
            })
            .catch(err => {
                dbPromise = null
                dbInstance = null
                throw err
            })
    }

    return dbPromise
}

/**
 * Synchronous accessor for code paths that are certain the DB is already open
 * (e.g. a caller that already awaited getDb()). Returns null otherwise.
 */
export const getDbSync = () => dbInstance

/**
 * Close the DB. Test / teardown use only.
 */
export const closeDb = async () => {
    if (dbInstance) {
        try {
            dbInstance.close()
        } catch {
            /* ignore */
        }
    }
    dbInstance = null
    dbPromise = null
}

/**
 * Drop the DB and any cached promise. Test use only.
 */
export const __resetDbForTests = async () => {
    await closeDb()
    if (typeof indexedDB !== 'undefined') {
        await new Promise(resolve => {
            const req = indexedDB.deleteDatabase(FARMDECK_DB_NAME)
            req.onsuccess = () => resolve()
            req.onerror = () => resolve()
            req.onblocked = () => resolve()
        })
    }
}
